import { minutesToTime } from 'components/TrainingPlanner/Participants/intervals';
import type { ColumnAvailability, TrainingPlannerColumn } from 'components/TrainingPlanner/types';
import { coversWindow } from 'components/TrainingPlanner/WeekGrid/availabilityCoverage';

/** 24:00 as a minute offset — the exclusive end of a weekday. */
export const DAY_MINUTES = 1440;

export const ALL_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

/**
 * Whether a lane can carry an appointment at this time.
 *
 * The single definition of "this court is open then" on the client, mirroring
 * `PlannerColumn::isOpenFor` on the server. Everything that offers, draws or
 * counts a lane goes through here, so a court cannot look open in the picker
 * and be refused on save.
 *
 * The hours are optional because plenty of callers only know the day — the week
 * grid deciding which lanes a Monday has, for one. Asked without them this
 * answers the old question, "is the lane open on that day at all", which is the
 * right question for drawing: a hall open on Monday evenings still needs its
 * Monday lane, or the appointments already on it would vanish.
 */
export const isColumnOpen = (
    column: Pick<TrainingPlannerColumn, 'availability'>,
    weekday: number,
    start?: number,
    end?: number
): boolean => {
    // Null is every day, all day — a lane the club never restricted.
    if (!column.availability) return true;

    if (start === undefined || end === undefined) {
        return column.availability.some((stretch) => stretch.weekday === weekday);
    }

    return coversWindow(column.availability, weekday, start, end);
};

/**
 * The same question including whether the lane is shown at all.
 *
 * A switched-off lane is not an unavailable one — it is a lane the grid hides —
 * but every caller offering a court has to rule out both, and forgetting the
 * second one saves appointments onto lanes nobody can see.
 */
export const isColumnUsable = (
    column: Pick<TrainingPlannerColumn, 'availability' | 'is_active'>,
    weekday?: number,
    start?: number,
    end?: number
): boolean => column.is_active && (weekday === undefined || isColumnOpen(column, weekday, start, end));

/**
 * The stretches of one weekday the lane is *not* open for, inside the window
 * the grid draws.
 *
 * The complement rather than the openings, because that is what gets shaded: an
 * empty list means nothing to grey out, which is exactly the unrestricted case.
 */
export const closedStretches = (
    column: Pick<TrainingPlannerColumn, 'availability'>,
    weekday: number,
    gridStart: number,
    gridEnd: number
): Array<{ start: number; end: number }> => {
    if (!column.availability || gridEnd <= gridStart) return [];

    const open = column.availability
        .filter((stretch) => stretch.weekday === weekday)
        .map((stretch) => ({
            start: Math.max(gridStart, stretch.start),
            end: Math.min(gridEnd, stretch.end)
        }))
        .filter((stretch) => stretch.end > stretch.start)
        .sort((left, right) => left.start - right.start);

    const closed: Array<{ start: number; end: number }> = [];
    let cursor = gridStart;

    open.forEach((stretch) => {
        if (stretch.start > cursor) closed.push({ start: cursor, end: stretch.start });
        cursor = Math.max(cursor, stretch.end);
    });

    if (cursor < gridEnd) closed.push({ start: cursor, end: gridEnd });

    return closed;
};

/** One from–to stretch of a day, in minutes since midnight. */
export interface HoursRange {
    start: number;
    end: number;
}

/**
 * One editable row per weekday, which is the shape the form works in.
 *
 * A day can hold several stretches ("Mo 07–10 und 18–20"). A day switched off
 * keeps its last stretches, so turning Saturday back on does not start from
 * empty fields.
 */
export interface ColumnHoursRow {
    weekday: number;
    isOpen: boolean;
    ranges: HoursRange[];
}

/** Fallback hours for a day being switched on: an ordinary training evening. */
export const DEFAULT_RANGE: HoursRange = { start: 16 * 60, end: 22 * 60 };

export const toHoursRows = (availability?: ColumnAvailability[] | null): ColumnHoursRow[] =>
    ALL_WEEKDAYS.map((weekday) => {
        // Unrestricted shows as every day, all day — the restriction is then
        // something you switch on rather than something to guess at.
        if (!availability) return { weekday, isOpen: true, ranges: [{ start: 0, end: DAY_MINUTES }] };

        const stated = availability
            .filter((stretch) => stretch.weekday === weekday)
            .sort((left, right) => left.start - right.start)
            .map(({ start, end }) => ({ start, end }));

        return stated.length
            ? { weekday, isOpen: true, ranges: stated }
            : { weekday, isOpen: false, ranges: [{ ...DEFAULT_RANGE }] };
    });

/** Whether a stretch is inverted or overlaps an earlier one of the same day. */
export const isRangeInvalid = (ranges: HoursRange[], index: number): boolean => {
    const range = ranges[index];
    if (range.end <= range.start) return true;

    return ranges.some((other, otherIndex) => otherIndex !== index && other.start < range.end && other.end > range.start);
};

export const hasInvalidRanges = (rows: ColumnHoursRow[]): boolean =>
    rows.some((row) => row.isOpen && row.ranges.some((_, index) => isRangeInvalid(row.ranges, index)));

/**
 * The rows as the server takes them, or null for "no restriction".
 *
 * Null rather than seven full days is the whole point: it is the only way to
 * tell a lane nobody ever restricted from one somebody happened to set to
 * 00:00–24:00, and every reader of this field distinguishes the two.
 */
export const fromHoursRows = (rows: ColumnHoursRow[]): ColumnAvailability[] | null => {
    const stretches = rows
        .filter((row) => row.isOpen)
        .flatMap((row) =>
            row.ranges
                .filter((range) => range.end > range.start)
                .map((range) => ({ weekday: row.weekday, start: range.start, end: range.end }))
        );

    const allDay =
        stretches.length === ALL_WEEKDAYS.length &&
        new Set(stretches.map((stretch) => stretch.weekday)).size === ALL_WEEKDAYS.length &&
        stretches.every((stretch) => stretch.start === 0 && stretch.end === DAY_MINUTES);

    return allDay ? null : stretches;
};

const formatRanges = (ranges: HoursRange[]) =>
    ranges.map((range) => `${minutesToTime(range.start)}-${minutesToTime(range.end)}`).join(', ');

const sameRanges = (left: HoursRange[], right: HoursRange[]) =>
    left.length === right.length &&
    left.every((range, index) => range.start === right[index].start && range.end === right[index].end);

/**
 * "Mo–Fr 16:00–22:00 · Sa 09:00–12:00, 14:00–18:00" for a table cell.
 *
 * Consecutive days that share their hours are collapsed into a range, because
 * the common case — a hall with one evening schedule all week — otherwise
 * spells out five identical rows and stops being readable at a glance.
 */
export const formatColumnAvailability = (
    column: Pick<TrainingPlannerColumn, 'availability'>,
    weekdayLabel: (weekday: number) => string,
    everyDayLabel: string,
    closedLabel = ''
): string => {
    if (!column.availability) return everyDayLabel;

    const rows = toHoursRows(column.availability).filter((row) => row.isOpen);
    // Stated and open on no day — a court imported from a space that is shut.
    // An empty cell reads as "not filled in yet", which is the opposite.
    if (!rows.length) return closedLabel;

    const groups: ColumnHoursRow[][] = [];

    rows.forEach((row) => {
        const last = groups[groups.length - 1];
        const previous = last?.[last.length - 1];

        if (previous && previous.weekday === row.weekday - 1 && sameRanges(previous.ranges, row.ranges)) {
            last.push(row);

            return;
        }

        groups.push([row]);
    });

    return groups
        .map((group) => {
            const first = group[0];
            const last = group[group.length - 1];
            // Two adjoining days read better as a pair than as a range of two.
            const days =
                group.length > 2
                    ? `${weekdayLabel(first.weekday)}-${weekdayLabel(last.weekday)}`
                    : group.map((row) => weekdayLabel(row.weekday)).join(', ');

            return `${days} ${formatRanges(first.ranges)}`;
        })
        .join(' · ');
};
